import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  Diamond, 
  Users, 
  TrendingUp, 
  Gift,
  CheckCircle,
  ExternalLink,
  AlertCircle,
  Loader2,
  Gem
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { multiNivelService } from '@/services/multi-nivel.service';

interface AdesaoMultiNivelProps {
  userData: any;
  onAdesaoCompleta: () => void;
}

const AdesaoMultiNivel: React.FC<AdesaoMultiNivelProps> = ({ userData, onAdesaoCompleta }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [aceitouTermos, setAceitouTermos] = useState(false);

  const handleAdesao = async () => {
    if (!aceitouTermos) {
      toast({
        title: "Termos não aceitos",
        description: "Você precisa aceitar os termos do programa para continuar.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await multiNivelService.criarAdesao({
        user_id: userData.id,
        observation: 'Adesão voluntária através do portal'
      });

      if (response.success) {
        toast({
          title: "Adesão realizada com sucesso!",
          description: "Agora você faz parte do programa de indicações.",
          variant: "default",
        });
        onAdesaoCompleta();
      } else {
        toast({
          title: "Erro na adesão",
          description: response.message || "Não foi possível realizar a adesão.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Erro ao fazer adesão:', error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const abrirTermos = () => {
    window.open('https://xdoinfinitto.com.br/assets/Termos e Regulamento Programa de Indicacao e Rede.pdf', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background pb-12">
      <div className="px-4 pt-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Programa de Indicações
            </h1>
            <p className="text-muted-foreground text-sm">
              Participe e ganhe diamantes extras
            </p>
          </div>
          <Badge variant="outline" className="text-primary border-primary bg-primary/5">
            {userData?.role === 'revendedora' ? 'Revendedor(a)' : 'Cliente'}
          </Badge>
        </div>

        {/* Card principal de adesão */}
        <Card className="mb-4 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Gem className="w-5 h-5 text-primary" />
              Faça parte do programa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Para acessar sua rede de indicações e acumular diamantes extras, 
              você precisa aderir ao nosso programa de indicações.
            </p>
          </CardContent>
        </Card>


        
        {/* Termos e adesão */}
        <Card className='mb-4'>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="termos"
                checked={aceitouTermos}
                onChange={(e) => setAceitouTermos(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="termos" className="text-sm text-muted-foreground cursor-pointer">
                Li e aceito os{' '}
                <button
                  onClick={abrirTermos}
                  className="text-primary underline inline-flex items-center gap-1"
                >
                  termos do programa de indicações
                  <ExternalLink className="w-3 h-3" />
                </button>
              </label>
            </div>

            <Button 
              onClick={handleAdesao}
              disabled={!aceitouTermos || loading}
              className="w-full"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Processando adesão...
                </>
              ) : (
                <>
                  <Users className="w-4 h-4 mr-2" />
                  Aderir ao programa
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Benefícios do programa */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Gift className="w-4 h-4" />
              Benefícios do programa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Ganhe diamantes por indicações</p>
                <p className="text-xs text-muted-foreground">
                  Receba diamantes quando suas indicações fazem compras ou vendas
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Sistema de rede</p>
                <p className="text-xs text-muted-foreground">
                  Ganhe também pelas indicações das suas indicações
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Acompanhe</p>
                <p className="text-xs text-muted-foreground">
                  Veja o crescimento da sua rede em tempo real
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Rankings e recompensas</p>
                <p className="text-xs text-muted-foreground">
                  Participe dos rankings mensais e resgate recompensas
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Como funciona */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <TrendingUp className="w-4 h-4" />
              Como funciona
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div>
                <p className="font-medium text-foreground">Compartilhe seu código</p>
                <p className="text-xs">Use seu código de indicação para convidar pessoas</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                2
              </div>
              <div>
                <p className="font-medium text-foreground">Eles fazem compras ou vendas</p>
                <p className="text-xs">Quando suas indicações compram ou vendem, você ganha diamantes</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                3
              </div>
              <div>
                <p className="font-medium text-foreground">Acumule e troque</p>
                <p className="text-xs">Use seus diamantes na Loja de Recompensas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Espaçamento para navegação mobile */}
      <div className="h-20 lg:h-0"></div>
    </div>
  );
};

export default AdesaoMultiNivel;
